/**
 * useCrypto Self-Encryption
 *
 * AES-256-GCM encryption/decryption for own device — avoids needing an X3DH
 * session with ourselves. Uses a key derived via HKDF from the identity private key.
 *
 * Extracted from useCrypto.ts for maintainability.
 * No circular imports — does NOT import from useCrypto.ts.
 */

import { toBase64, fromBase64 } from "@kollegeplus/crypto";

// ─── Self-Encrypt ─────────────────────────────────────────────────────────────

/**
 * Encrypt plaintext for own device using a key derived from the identity private key.
 * Returns a base64-encoded JSON blob containing IV and ciphertext.
 */
export async function selfEncrypt(plaintext: string, identityPrivateKey: Uint8Array): Promise<string> {
  const enc = new TextEncoder();
  // Ensure we have a plain ArrayBuffer (SubtleCrypto requires it)
  const keyBytes = new Uint8Array(identityPrivateKey).buffer as ArrayBuffer;

  // Derive a self-encryption key via HKDF
  const keyMaterial = await crypto.subtle.importKey(
    "raw", keyBytes, "HKDF", false, ["deriveKey"],
  );
  const selfKey = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: enc.encode("kollegeplus-self-encrypt"), info: enc.encode("self-msg") },
    keyMaterial, { name: "AES-GCM", length: 256 }, false, ["encrypt"],
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    selfKey,
    enc.encode(plaintext),
  );

  // Pack as base64 JSON: { iv, ct }
  return JSON.stringify({
    iv: toBase64(new Uint8Array(iv)),
    ct: toBase64(new Uint8Array(ciphertextBuf)),
  });
}

// ─── Self-Decrypt ─────────────────────────────────────────────────────────────

/**
 * Decrypt a selfEncrypt()-produced blob for own device.
 */
export async function selfDecrypt(packed: string, identityPrivateKey: Uint8Array): Promise<string> {
  const enc = new TextEncoder();
  const dec = new TextDecoder();

  const { iv: ivB64, ct: ctB64 } = JSON.parse(packed);
  const keyBytes = new Uint8Array(identityPrivateKey).buffer as ArrayBuffer;

  const keyMaterial = await crypto.subtle.importKey(
    "raw", keyBytes, "HKDF", false, ["deriveKey"],
  );
  const selfKey = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: enc.encode("kollegeplus-self-encrypt"), info: enc.encode("self-msg") },
    keyMaterial, { name: "AES-GCM", length: 256 }, false, ["decrypt"],
  );

  const ivBytes = fromBase64(ivB64);
  const ctBytes = fromBase64(ctB64);
  const plaintextBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(ivBytes).buffer as ArrayBuffer },
    selfKey,
    new Uint8Array(ctBytes).buffer as ArrayBuffer,
  );

  return dec.decode(plaintextBuf);
}
